import { ReactNode } from "react";
import { extendProps } from "../../../helpers/common";
import IconButton from "../../atoms/icon-button/IconButton";
import LinkButton, { LinkButtonProps } from "../../atoms/link-button/LinkButton";
import {
    Box,
    Card,
    Stack,
    BoxProps,
    CardProps,
    StackProps,
    Typography,
    IconButtonProps,
    TypographyProps,
} from "@mui/material";

export interface NavCardProps {
    heading?: string;
    children?: ReactNode;
    cardProps?: CardProps;
    topStackProps?: StackProps;
    headingProps?: TypographyProps;
    backButtonProps?: IconButtonProps;
    headingLinkProps?: LinkButtonProps;
    onBack?: () => void;
}

const backArrowIcon = (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9 14L5 10L9 6" stroke="#2A084B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path
            d="M5 10H16C17.0609 10 18.0783 10.4214 18.8284 11.1716C19.5786 11.9217 20 12.9391 20 14C20 15.0609 19.5786 16.0783 18.8284 16.8284C18.0783 17.5786 17.0609 18 16 18H15"
            stroke="#2A084B"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

function NavCard(props: NavCardProps) {
    const { cardProps, children, heading, topStackProps, headingProps, headingLinkProps, backButtonProps } = props;

    const newCardProps = extendProps(cardProps || {}, {
        children,
        sx: {
            px: 3,
            py: 5,
            border: 1,
            borderRadius: 1.5,
            position: "relative",
            borderColor: "primary.main",
        },
    });

    const newTopStackProps = extendProps(topStackProps || {}, {
        sx: {
            gap: 1,
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
        },
    });

    const newHeadingProps = extendProps(headingProps || {}, {
        variant: "h4",
        sx: {
            flex: 1,
            textAlign: "center",
        },
        children: heading,
    });

    const newHeadingLinkProps = extendProps(headingLinkProps || {}, {
        sx: {
            marginLeft: "auto",
            marginRight: 1,
        },
    });

    const newBackButtonProps = extendProps(backButtonProps || {}, {
        sx: {
            padding: "6px",
            borderRadius: 0,
            backgroundColor: "background.default",
        },
        onClick: props.onBack,
    });

    const buttonWrapperProps: BoxProps = {
        sx: {
            top: 0,
            left: 0,
            border: 1,
            borderTop: 0,
            borderLeft: 0,
            overflow: "hidden",
            position: "absolute",
            borderColor: "primary.main",
            borderBottomRightRadius: "12px",
        },
    };

    return (
        <Stack gap={1}>
            {(heading || headingLinkProps) && (
                <Stack {...newTopStackProps}>
                    {heading && <Typography {...newHeadingProps} />}
                    {headingLinkProps && <LinkButton {...newHeadingLinkProps} />}
                </Stack>
            )}
            {children && (
                <Card {...newCardProps}>
                    {props.onBack && (
                        <Box {...buttonWrapperProps}>
                            <IconButton {...newBackButtonProps}>{backArrowIcon}</IconButton>
                        </Box>
                    )}
                    {newCardProps.children}
                </Card>
            )}
        </Stack>
    );
}

export default NavCard;
